import { Heading } from '@mm/components';
import { useTagsCRUDActions } from '@mm/state/actions/TagsCRUDActions';
import { TagFilterItem, TagFilterValue } from '@mm/state/model/AudiencesModel';
import { allTags } from '@mm/state/selectors/tags';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormGroup } from 'reactstrap';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { FilterableMultiSelect } from '../../../../../../common/components/FilterableMultiSelect/FilterableMultiSelect';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '../../../../../../common/modals/modal-helpers';
import { AddFilterType, UpdateFilterType } from '../../../sharedFilterTypes';
import './tagsModal.scss';

type Props = {
  toggle: () => void;
  onFinish: AddFilterType;
  onUpdateFilter: UpdateFilterType;
  selectedTag?: TagFilterItem;
  selectedTagId?: number | null;
  nonModalUse?: boolean;
  className?: string;
};

export const TagsModal = ({
  onUpdateFilter,
  toggle,
  onFinish,
  selectedTagId,
  selectedTag,
  nonModalUse,
  className,
}: Props) => {
  const availableTags = useSelector(allTags);
  const fbPageId = useSelector(activeFacebookPageIdSelector);

  const tagsCRUDActions = useTagsCRUDActions();
  const getAllTags = (pageId: number) =>
    tagsCRUDActions.getAll(pageId, false, ['otn', 'regular']);

  const [isSaving, setIsSaving] = useState(false);
  const [tags, setTags] = useState<TagFilterValue[]>(
    (selectedTag && selectedTag.values) || [],
  );
  const tagsHeader = useMemo(() => {
    return tags.length
      ? tags
          .map(({ name }) => name)
          .slice(0, 10)
          .join(', ')
      : 'Select tag(s)';
  }, [tags]);

  const onSelectChange = useCallback((e: string[]) => {
    const selectedTags: TagFilterValue[] = e.map(selectedTag => {
      const match = selectedTag.match(/ \(otn\)/);
      if (match !== null) {
        return { name: selectedTag.substr(0, match.index), kind: 'otn' };
      }
      return { name: selectedTag, kind: 'regular' };
    });
    setTags(selectedTags);
  }, []);

  const [operator, setOperator] = useState(
    (selectedTag && selectedTag.operator) || 'include',
  );

  const onOperatorChange = useCallback(e => {
    setOperator(e.target.value);
  }, []);

  useEffect(() => {
    if (fbPageId) {
      getAllTags(fbPageId);
    }
  }, [fbPageId]);

  const suggestions = useMemo(() => {
    return availableTags
      .filter(
        e =>
          tags.find(({ name, kind }) => e.name === name && e.kind === kind) ===
          undefined,
      )
      .map(({ name, kind }) => {
        const key = kind === 'otn' ? `${name || ''} (otn)` : name || '';
        return { key, name: key };
      });
  }, [availableTags, tags]);

  const onClose = useCallback(() => {
    toggle();
  }, []);

  const onSave = useCallback(() => {
    setIsSaving(true);

    onFinish(undefined, {
      values: tags,
      operator,
      filterType: 'tags_filters',
    });
    onClose();
  }, [tags, operator]);

  const onUpdate = useCallback(() => {
    setIsSaving(true);

    if (selectedTagId) {
      onUpdateFilter(
        selectedTagId,
        {
          values: tags,
          operator,
          filterType: 'tags_filters',
        },
        true,
      );
    }
    onClose();
  }, [tags, operator]);

  const header = (
    <span className="tags-filter-modal__header-content">
      {selectedTag ? 'Update' : 'Create'} Filter based on Tag
    </span>
  );

  const bodyForm = (
    <>
      <FormGroup>
        <div className="tags-filter-modal__label">
          <Heading
            className="tags-filter-modal__title"
            size={5}
            text="Choose filter type"
          />
          <div className="tags-filter-modal__label-group">
            <label className="reachable-filter-modal__input-container">
              <input
                type="radio"
                value="include"
                name="operator"
                defaultChecked={operator === 'include'}
                onChange={onOperatorChange}
                className="reachable-filter-modal__radio-input"
              />
              Include
            </label>
            <label className="reachable-filter-modal__input-container">
              <input
                type="radio"
                name="operator"
                value="exclude"
                defaultChecked={operator === 'exclude'}
                onChange={onOperatorChange}
                className="reachable-filter-modal__radio-input"
              />
              Exclude
            </label>
          </div>
        </div>
      </FormGroup>
      <FormGroup>
        <label className="tags-filter-modal__label">
          <Heading
            className="tags-filter-modal__title"
            size={5}
            text="Tag(s)"
          />
          <FilterableMultiSelect
            heading={tagsHeader}
            selectedKeys={tags.map(({ name, kind }) =>
              kind === 'otn' ? `${name} (otn)` : name,
            )}
            closeByChange
            searchPlaceholder="Search tag(s)"
            onChange={onSelectChange}
            className="tags-filter-modal__autocomplete"
            items={suggestions}
            inputClassName="tags-filter-modal__search-input--input"
          />
        </label>
      </FormGroup>
    </>
  );

  const footer = (
    <div className="tags-filter-modal__buttons">
      <PrimaryButton color={ButtonColor.secondaryBlue} onClick={onClose}>
        Cancel
      </PrimaryButton>
      {!selectedTag && (
        <PrimaryButton disabled={isSaving || !tags.length} onClick={onSave}>
          Save
        </PrimaryButton>
      )}
      {selectedTag && (
        <PrimaryButton disabled={isSaving || !tags.length} onClick={onUpdate}>
          Update
        </PrimaryButton>
      )}
    </div>
  );

  let content = (
    <>
      <ModalHeader className="tags-filter-modal__header">{header}</ModalHeader>
      <ModalBody className="tags-filter-modal__container">{bodyForm}</ModalBody>
      <ModalFooter className="tags-filter-modal__footer">{footer}</ModalFooter>
    </>
  );

  if (nonModalUse) {
    content = (
      <>
        {bodyForm}
        {footer}
      </>
    );
  }

  return (
    <div className={classNames(className, 'tags-filter-modal')}>{content}</div>
  );
};
